import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, Tooltip } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import LayersIcon from '@material-ui/icons/Layers'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import {
  useGetAiModels,
  useGetAiModelProviders,
  useInsertAiModel,
  useUpdateAiModel,
  useDeleteAiModel
} from '../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../Analysis/SmartEndpoints/formatRelativeTime'
import { useListStyles } from './useListStyles'
import { MODEL_FIELDS } from './fieldSchemas'
import { useToast } from './ToastContext'
import RecordDrawer from './RecordDrawer'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'

function ModelsTab ({ providerFilter, setProviderFilter, onDrilldownToTasks }) {
  const classes = useListStyles()
  const { showToast, showError } = useToast()
  const { data, isLoading } = useGetAiModels({ admin: true })
  const { data: providersData } = useGetAiModelProviders()
  const insertMutation = useInsertAiModel()
  const updateMutation = useUpdateAiModel()
  const deleteMutation = useDeleteAiModel()

  const [searchText, setSearchText] = useState('')
  const [drawer, setDrawer] = useState({ open: false, record: null })
  const [confirm, setConfirm] = useState(null)

  const providers = useMemo(() => providersData?.providers || [], [providersData])
  const providerNameById = useMemo(() => new Map(providers.map(p => [p.providerId, p.name])), [providers])

  const tableData = useMemo(() => {
    const models = data?.models || []
    return models
      .filter(m => !providerFilter || m.providerId === providerFilter)
      .map(m => ({
        ...m,
        enabled: !!m.enabled,
        hidden: !!m.hidden,
        providerName: m.provider?.name || providerNameById.get(m.providerId) || m.providerId,
        lastModified: m.modifiedDate || m.createdDate || null,
        lastModifiedBy: m.modifiedBy || m.createdBy || null
      }))
  }, [data, providerFilter, providerNameById])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'modelId',
      accessor: 'modelId',
      Header: 'ID',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'name', accessor: 'name', Header: 'Name' },
    { id: 'providerName', accessor: 'providerName', Header: 'Provider' },
    {
      id: 'enabled',
      accessor: 'enabled',
      Header: 'Enabled',
      Cell: ({ value }) => value
        ? <span className={classes.enabledBadge}>enabled</span>
        : <span className={classes.disabledBadge}>disabled</span>
    },
    {
      id: 'hidden',
      accessor: 'hidden',
      Header: 'Hidden',
      Cell: ({ value }) => value
        ? <span className={classes.warnBadge}>hidden</span>
        : <span className={classes.disabledBadge}>visible</span>
    },
    {
      id: 'inputPriceFactor',
      accessor: 'inputPriceFactor',
      Header: 'In $/1K',
      Cell: ({ value }) => <span className={classes.mono}>{value == null ? '—' : Number(value).toFixed(6)}</span>
    },
    {
      id: 'outputPriceFactor',
      accessor: 'outputPriceFactor',
      Header: 'Out $/1K',
      Cell: ({ value }) => <span className={classes.mono}>{value == null ? '—' : Number(value).toFixed(6)}</span>
    },
    { id: 'maxTokens', accessor: 'maxTokens', Header: 'Max Tokens' },
    { id: 'requestsPerMinute', accessor: 'requestsPerMinute', Header: 'RPM' },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          {onDrilldownToTasks && (
            <Tooltip title='View tasks using this model' arrow>
              <IconButton
                size='small'
                className={classes.actionBtn}
                onClick={() => onDrilldownToTasks(row.original.modelId)}
              >
                <LayersIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Edit model' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => setDrawer({ open: true, record: row.original })}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Delete model' arrow>
            <IconButton
              size='small'
              className={classes.dangerBtn}
              onClick={() => setConfirm({ modelId: row.original.modelId })}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ], [classes, onDrilldownToTasks])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'providerName', desc: false }], [])

  const drawerOptions = useMemo(() => ({
    providers: providers.map(p => ({ value: p.providerId, label: `${p.name} (${p.providerId})` }))
  }), [providers])

  const handleDelete = useCallback(async () => {
    if (!confirm) return
    try {
      await deleteMutation.mutateAsync({ modelId: confirm.modelId })
      showToast('Model deleted')
    } catch (err) {
      showError(err, 'Delete failed')
    } finally {
      setConfirm(null)
    }
  }, [confirm, deleteMutation, showToast, showError])

  const isMutating = insertMutation.isLoading || updateMutation.isLoading
  const openNew = () => setDrawer({ open: true, record: null })
  const allCount = (data?.models || []).length

  return (
    <>
      <div className={classes.newBtnWrap}>
        <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Model</SydButton>
      </div>

      {isLoading ? (
        <Loading />
      ) : allCount === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateMessage}>
            No AI models yet. Create one to get started.
          </div>
          <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Model</SydButton>
        </div>
      ) : (
        <>
          <div className={classes.toolbar}>
            <span className={classes.recordCount}>
              {tableData.length} of {allCount} {allCount === 1 ? 'model' : 'models'}
            </span>
            <div className={classes.toolbarRight}>
              <select
                className={classes.filterSelect}
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
              >
                <option value=''>All providers</option>
                {providers.map(p => <option key={p.providerId} value={p.providerId}>{p.name}</option>)}
              </select>
              <input
                className={classes.searchInput}
                placeholder='Search models…'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>
          <div className={classes.tableWrap}>
            <OperationalTable
              variant='blank'
              itemName='AI Models'
              columns={columns}
              data={tableData}
              defaultSort={defaultSort}
              searchText={searchText}
            />
          </div>
        </>
      )}

      <RecordDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, record: null })}
        title={drawer.record ? (drawer.record.name || drawer.record.modelId) : 'New Model'}
        entityLabel='Model'
        fields={MODEL_FIELDS}
        options={drawerOptions}
        record={drawer.record}
        isMutating={isMutating}
        onInsert={(payload) => insertMutation.mutateAsync(payload)}
        onUpdate={(payload) => updateMutation.mutateAsync({ ...payload, modelId: drawer.record.modelId })}
      />

      <ConfirmDeleteDialog
        open={!!confirm}
        entityLabel='Model'
        recordId={confirm?.modelId}
        isMutating={deleteMutation.isLoading}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </>
  )
}

ModelsTab.propTypes = {
  providerFilter: PropTypes.string.isRequired,
  setProviderFilter: PropTypes.func.isRequired,
  onDrilldownToTasks: PropTypes.func
}

ModelsTab.defaultProps = {
  onDrilldownToTasks: undefined
}

export default ModelsTab
